import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { TextInput } from '../../../components';
import Text from '../../../components/text/Text';
import { PrimaryButton, Sheet } from '../../../design/components';
import { GET_SERVICE_RUN_VEHICLES } from '../../../graphql/vehicle';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import usePagedSearchQuery from '../../../hooks/usePagedSearchQuery';
import { getVehicleText } from '../../servicerun/ServiceRunDetail/ServiceRunVehicleDetail';

const SHORT_LIST_COUNT = 4;
const VEHICLE_FILTER = { isExcludedFromServiceRuns: false };

const VehicleOption = memo(({ vehicle, isSelected, isAssigned, onSelect }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const details = [
		vehicle.registration ? `Rego ${vehicle.registration}` : null,
		isAssigned ? 'Assigned to this run' : null,
	].filter(Boolean).join(' · ');

	return (
		<Pressable
			onPress={() => onSelect(vehicle)}
			accessibilityRole="radio"
			accessibilityLabel={[vehicle.name || 'Un-named vehicle', details].filter(Boolean).join(', ')}
			accessibilityState={{ selected: isSelected, checked: isSelected }}
			style={({ pressed }) => [styles.option, isSelected && styles.optionSelected, pressed && styles.pressed]}
		>
			<Feather name="truck" size={22} color={isSelected ? colors.accent : colors.iconMuted} />
			<View style={styles.optionBody}>
				<Text style={styles.optionTitle}>{vehicle.name || vehicle.registration || 'Un-named vehicle'}</Text>
				{Boolean(details) && <Text style={styles.optionDetails}>{details}</Text>}
			</View>
			<View style={[styles.radio, isSelected && styles.radioSelected]}>
				{isSelected && <View style={styles.radioDot} />}
			</View>
		</Pressable>
	);
});

/**
 * Confirm vehicle: pick today's vehicle from the fleet list, the run's
 * assigned vehicle preselected and first. Confirming the assigned vehicle
 * confirms it; choosing another one assigns and confirms that one.
 * Props:
 * - isVisible, onClose
 * - assignedVehicle: the run's vehicle ({ id, name, registration }) or null
 * - onSelectVehicle(vehicle), onConfirmVehicle(): useDriverDayActions selectVehicle / confirmVehicle
 * - loading, disabled (training offline)
 */
const ChooseVehicleSheet = ({
	isVisible,
	onClose,
	assignedVehicle,
	onSelectVehicle,
	onConfirmVehicle,
	loading,
	disabled,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const {
		search,
		setSearch,
		query,
		resetSearch,
		list,
		items: fleet,
		count: fleetCount,
		loading: isLoadingVehicles,
		error,
		loadMore,
	} = usePagedSearchQuery({
		document: GET_SERVICE_RUN_VEHICLES,
		field: 'getVehicles',
		filter: VEHICLE_FILTER,
		isActive: isVisible,
	});
	const [selectedVehicle, setSelectedVehicle] = useState(assignedVehicle || null);
	// Read on open only, so a driver day refresh never resets the driver's pick.
	const assignedVehicleRef = useRef(assignedVehicle);
	assignedVehicleRef.current = assignedVehicle;

	useEffect(() => {
		if (isVisible) {
			resetSearch();
			setSelectedVehicle(assignedVehicleRef.current?.id ? assignedVehicleRef.current : null);
		}
	}, [isVisible, resetSearch]);

	const [unsearchedFleetCount, setUnsearchedFleetCount] = useState(0);
	useEffect(() => {
		if (!query && list) {
			setUnsearchedFleetCount(fleetCount);
		}
	}, [fleetCount, list, query]);

	// Without a search the assigned vehicle leads the list (and stays there offline).
	const vehicles = useMemo(() => {
		const others = (fleet || []).filter(vehicle => vehicle.id !== assignedVehicle?.id);
		if (query) {
			return fleet || [];
		}
		return assignedVehicle?.id ? [assignedVehicle, ...others] : others;
	}, [assignedVehicle, fleet, query]);

	const confirm = useCallback(() => {
		if (!selectedVehicle) {
			return;
		}
		if (selectedVehicle.id === assignedVehicle?.id) {
			onConfirmVehicle();
			return;
		}
		onSelectVehicle(selectedVehicle);
	}, [assignedVehicle?.id, onConfirmVehicle, onSelectVehicle, selectedVehicle]);

	const renderVehicle = useCallback(({ item }) => (
		<VehicleOption
			vehicle={item}
			isSelected={item.id === selectedVehicle?.id}
			isAssigned={item.id === assignedVehicle?.id}
			onSelect={setSelectedVehicle}
		/>
	), [assignedVehicle?.id, selectedVehicle?.id]);

	let listNote = null;
	if (fleet && !vehicles.length) {
		listNote = query ? 'No vehicle matches this name.' : 'No vehicles are set up for service runs.';
	}
	else if (!fleet && error && !isLoadingVehicles) {
		listNote = 'Connect to load the other vehicles.';
	}

	// Short fleets keep the compact sheet; longer lists use the full height. The size
	// follows the whole fleet (last list without a search), so typing keeps it steady.
	const isFullHeight = unsearchedFleetCount > SHORT_LIST_COUNT;

	return (
		<Sheet
			isVisible={isVisible}
			onClose={onClose}
			title="Choose your vehicle"
			fullHeight={isFullHeight}
			scroll={false}
			footer={(
				<PrimaryButton
					label={selectedVehicle ? `Confirm ${getVehicleText(selectedVehicle)}` : 'Choose a vehicle'}
					onPress={confirm}
					loading={loading}
					disabled={disabled || !selectedVehicle}
				/>
			)}
		>
			<TextInput
				value={search}
				onChange={setSearch}
				placeholder="Search vehicle name"
				autoCorrect={false}
				autoCapitalize="none"
				accessibilityLabel="Search vehicle name"
			/>
			<FlatList
				data={vehicles}
				keyExtractor={vehicle => vehicle.id}
				renderItem={renderVehicle}
				extraData={selectedVehicle?.id}
				onEndReached={loadMore}
				onEndReachedThreshold={0.5}
				keyboardShouldPersistTaps="handled"
				style={isFullHeight ? styles.listFull : styles.list}
				contentContainerStyle={styles.listContent}
				ListFooterComponent={(
					<>
						{isLoadingVehicles && <ActivityIndicator color={colors.accent} style={styles.loading} />}
						{Boolean(listNote) && <Text style={styles.note}>{listNote}</Text>}
					</>
				)}
			/>
		</Sheet>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	list: {
		flexGrow: 0,
	},
	listFull: {
		flex: 1,
	},
	listContent: {
		gap: space.sm,
	},
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 64,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: radius.hero,
		borderWidth: 1,
		borderColor: colors.border,
		backgroundColor: colors.card,
	},
	optionSelected: {
		borderWidth: 2,
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	pressed: {
		opacity: 0.85,
	},
	optionBody: {
		flex: 1,
		gap: 2,
	},
	optionTitle: {
		...type.bodyStrong,
		color: colors.text,
	},
	optionDetails: {
		...type.secondary,
		color: colors.textSecondary,
	},
	radio: {
		width: 26,
		height: 26,
		borderRadius: radius.pill,
		borderWidth: 2,
		borderColor: colors.iconMuted,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioSelected: {
		borderColor: colors.currentBorder,
	},
	radioDot: {
		width: 12,
		height: 12,
		borderRadius: radius.pill,
		backgroundColor: colors.currentBorder,
	},
	loading: {
		paddingVertical: space.lg,
	},
	note: {
		...type.secondary,
		color: colors.textSecondary,
		textAlign: 'center',
		paddingVertical: space.sm,
	},
});

export default ChooseVehicleSheet;
